import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { Organization, OrganizationGroup, UsersService } from '../users/users.service';
import { OrganizationGroupService } from '../organization-group/organization-group.service';
import { UserRole, UserRoleService } from '../user-role/user-role.service';

@Injectable({ providedIn: 'root' })
export class CurrentUserContextService {
  readonly isGroupLevelUser$: Observable<boolean>;
  readonly displayOrganization$: Observable<Organization | null>;
  readonly displayOrganizationGroup$: Observable<OrganizationGroup | null>;

  constructor(
    private readonly usersService: UsersService,
    private readonly orgGroupService: OrganizationGroupService,
    private readonly userRoleService: UserRoleService,
  ) {
    this.isGroupLevelUser$ = this.userRoleService.userRole$.pipe(
      map((r) => r === UserRole.SUPER_ADMIN || r === UserRole.SUPERVISOR),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const context$ = combineLatest([
      this.isGroupLevelUser$,
      this.usersService.currentUser$,
      this.orgGroupService.activeOrg$,
      this.orgGroupService.activeGroup$,
    ]);

    this.displayOrganization$ = context$.pipe(
      map(([isGroupLevel, me, activeOrg]) =>
        isGroupLevel ? (activeOrg ?? null) : (me?.ORGANIZATION ?? null),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.displayOrganizationGroup$ = context$.pipe(
      map(([isGroupLevel, me, _activeOrg, activeGroup]) =>
        isGroupLevel
          ? (activeGroup ?? me?.ORGANIZATION_GROUP ?? null)
          : (me?.ORGANIZATION_GROUP ?? null),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}
